import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../../shadcn/components/ui/card';
import { Button } from '../../../shadcn/components/ui/button';
import {
  ArrowLeft,
  User,
  Mail,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  FileText,
  Info,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Phone,
  MessageSquare,
} from 'lucide-react';
import { useAppStore } from 'store/appStore';
import { formatTimestamp } from '../utils/utils';
import { ConversationMessage, FetchMeetingFullReportResponse } from 'api-types';
import { findTopMostCategory } from '../slice/meetingSlice';
import { getQualificationSentimentType, getSentimentStyles } from '../utils/sentimentUtils';
import { SpinnerLoader } from 'components/Loader/SpinnerLoader';
import { ErrorCard } from '../../../components/ErrorHandling';
import { getMeetingReportUIConfig } from './meetingReportConfig';

export function CallReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportType = searchParams.get('type') || 'voice';
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedSections, setExpandedSections] = useState({
    overallFeedback: true,
    criteriaFeedback: true,
    visaEligibility: true,
    clientInformation: true,
    transcript: true,
    candidateInformation: true,
    meetingVideo: true,
  });
  const { reportId, meetingId, pageParam } = useParams();
  const uiConfig = getMeetingReportUIConfig(meetingId);
  const fetchMeetingFullReport = useAppStore(state => state.fetchMeetingFullReport);
  const fetchWhatsappFullReport = useAppStore(state => state.fetchWhatsappFullReport);
  const fetchMeetingAudioUrl = useAppStore(state => state.fetchMeetingAudioUrl);
  const [meetingFullReport, setMeetingFullReport] = useState<FetchMeetingFullReportResponse | null>(
    null
  );
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGoBack = () => {      
    if (window.opener || window.history.length <= 1) {
      window.close();                                                                                 
    } else {
      navigate(`/meeting-report/${meetingId}/${pageParam || '1'}`);                                   
    }                                                                 
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    if (reportId) {
      setLoading(true);
      setError(null);

      const fetchFunction =
        reportType === 'whatsapp'
          ? () => fetchWhatsappFullReport({ conversationId: reportId })
          : () => fetchMeetingFullReport({ assessmentId: reportId, meetingId: meetingId });

      fetchFunction()
        .then(result => setMeetingFullReport(result))
        .catch(() => {
          setMeetingFullReport(null);
          setError(`Error getting ${reportType} full report try again later`);
        })
        .finally(() => setLoading(false));
    }
  }, [reportId, meetingId, reportType, fetchMeetingFullReport, fetchWhatsappFullReport]);

  const assessment = meetingFullReport?.assessment;

  useEffect(() => {
    if (
      reportType === 'voice' &&
      expandedSections.meetingVideo &&
      assessment?.id &&
      !audioUrl &&
      !audioLoading
    ) {
      setAudioLoading(true);
      setAudioError(null);
      fetchMeetingAudioUrl({ assessmentId: assessment.id, meetingId: meetingId })
        .then(res => {
          setAudioUrl(res.audioUrl || '');
        })
        .catch(err => {
          setAudioError('Failed to load audio');
          setAudioUrl(null);
        })
        .finally(() => {
          setAudioLoading(false);
        });
    }
  }, [expandedSections.meetingVideo, assessment?.id, fetchMeetingAudioUrl, reportType]);

  if (loading) {
    return <SpinnerLoader />;
  }

  if (error || !assessment) {
    return (
      <div className="max-w-7xl mx-auto pb-16">
        <ErrorCard
          title={error ? 'Unable to load call report' : 'Assessment Not Found'}
          message={
            error ||
            'The requested assessment could not be found. It may have been deleted or you may not have permission to view it.'
          }
          onRetry={error ? () => window.location.reload() : undefined}
          onBack={handleGoBack}
          showBackButton={true}
          showRetryButton={!!error}
        />
      </div>
    );
  }

  const topMostCategory = findTopMostCategory(assessment.evaluationContent || {});

  const getName = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) {
      return 'N/A';
    }
    return `${firstName || ''} ${lastName || ''}`.trim();
  };

  return (
    <div className="max-w-7xl mx-auto pb-16">
      <div className="mb-6">
        <Button
          onClick={handleGoBack}
          variant="ghost"
          className="mb-4 p-2 h-10 flex items-center gap-2 justify-center text-gray-700 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-base font-medium">Back</span>
        </Button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Full Report
            </h1>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm w-full">
        <div className="flex items-center mb-4">
          <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
            <User className="h-8 w-8" />
          </div>
          <div className="ml-4 flex-1">
            <h2 className="text-xl font-semibold text-gray-900">
              {getName(assessment.interviewee?.firstName, assessment.interviewee?.lastName)}
            </h2>
            <div className="flex items-center text-gray-600 mt-1">
              <Mail className="h-4 w-4 mr-2" />
              {assessment.interviewee?.email || 'No email provided'}
            </div>
            <div className="flex items-center text-gray-600 mt-1">
              <Phone className="h-4 w-4 mr-2" />
              {assessment.interviewee?.phone || 'No phone number provided'}
            </div>
            <div className="flex items-center text-gray-600 mt-1">
              <MessageSquare className="h-4 w-4 mr-2" />
              {assessment.interviewee?.whatsapp || 'No WhatsApp number provided'}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-gray-500">Start Time</div>
            <div className="mt-1  mb-4">
              {assessment.startedAt ? <div>{getStartTime(assessment.startedAt)}</div> : 'N/A'}
            </div>

            <div className="text-sm font-semibold text-gray-500">Duration</div>
            <div className="mt-1 mb-4">
              {assessment.startedAt && assessment.endedAt
                ? getDuration(assessment.startedAt, assessment.endedAt)
                : 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {uiConfig.showVisaCategorySection && topMostCategory && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm w-full">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Visa Category</h2>
          <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
            <span>{topMostCategory.criteria}</span>
            <div
              className={`text-md font-semibold rounded-full px-3 py-1 ${getBadgeClass(topMostCategory.qualificationScore)}`}
            >
              {topMostCategory.qualificationScore}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm w-full">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Overall Feedback</h2>
        <div className="border-t border-gray-100 pt-4">
          {assessment.evaluationContent?.overallFeedback || 'No feedback provided'}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm w-full">
        <div
          className="flex items-center justify-between mb-4 cursor-pointer"
          onClick={() => toggleSection('candidateInformation')}
        >
          <h2 className="text-xl font-semibold text-gray-900">Candidate Information</h2>
          {expandedSections.candidateInformation ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
        {expandedSections.candidateInformation && (
          <div className="border-t border-gray-100 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {assessment.evaluationContent?.candidateInformation ||
                    'No candidate information available'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {uiConfig.showVisaCategoriesSection &&
      assessment.evaluationContent?.criteriaFeedback?.length ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm w-full">
          <div
            className="flex items-center justify-between mb-4 cursor-pointer"
            onClick={() => toggleSection('criteriaFeedback')}
          >
            <h2 className="text-xl font-semibold text-gray-900">Visa Categories</h2>
            {expandedSections.criteriaFeedback ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </div>
          <div className="border-t border-gray-100 pt-4">
            {assessment.evaluationContent.criteriaFeedback.map((criterion: any, index: number) => {
              return (
                <div
                  key={index}
                  className="mb-4 last:mb-0 pb-4 last:pb-0 border-b last:border-b-0 border-gray-100"
                >
                  <div className="mb-2 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-900">{criterion.criteria || 'N/A'}</h3>
                    <div
                      className={`text-md font-semibold rounded-full px-3 py-1 ${getBadgeClass(
                        criterion.qualificationScore
                      )}`}
                    >
                      {criterion.qualificationScore}
                    </div>
                  </div>
                  <div className={`rounded-md`}>
                    <p className="whitespace-pre-wrap">
                      {criterion.feedback || 'No feedback provided'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        ''
      )}

      {reportType === 'voice' && assessment.videoUrl && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm w-full">
          <div
            className="flex items-center justify-between mb-4 cursor-pointer"
            onClick={() => toggleSection('meetingVideo')}
          >
            <h2 className="text-xl font-semibold text-gray-900">Recording</h2>
            {expandedSections.meetingVideo ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </div>
          {expandedSections.meetingVideo && (
            <div className="border-t border-gray-100 pt-4">
              {audioLoading ? (
                <div className="flex items-center justify-center py-6 bg-gray-50 rounded-lg">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-700 mr-3"></div>
                  <span className="text-gray-600 font-medium">Loading audio recording...</span>
                </div>
              ) : audioError ? (
                <ErrorCard
                  title="Audio Loading Failed"
                  message={audioError}
                  showBackButton={false}
                  variant="destructive"
                />
              ) : audioUrl ? (
                <audio controls className="w-full rounded-lg">
                  <source src={audioUrl} type="audio/wav" />
                  Your browser does not support the audio tag.
                </audio>
              ) : (
                <div className="text-center text-gray-500 py-6 bg-gray-50 rounded-lg">
                  <Info className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                  <p>No audio recording available for this meeting</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm w-full">
        <div
          className="flex items-center justify-between mb-4 cursor-pointer"
          onClick={() => toggleSection('transcript')}
        >
          <h2 className="text-xl font-semibold text-gray-900">Transcript</h2>
          {expandedSections.transcript ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>

        {expandedSections.transcript && (
          <div className="border-t border-gray-100 pt-4 mt-3 gap-5">
            {assessment.transcriptionContent && assessment.transcriptionContent.length > 0 ? (
              assessment.transcriptionContent.map((entry: any, index: number) => (
                <div key={index} className="mb-6 last:mb-0">
                  {(entry.role === 'Agent' || entry.role === 'assistant') &&
                    (entry.text || entry.content) && (
                      <div className="mb-2">
                        <div className="flex items-start">
                          <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-white mr-3">
                            <MessageCircle className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <div className="font-medium text-gray-900">Arka Agent</div>
                              <div className="text-xs text-gray-500">
                                {entry.timestamp ? formatTimestamp(entry.timestamp) : ''}
                              </div>
                            </div>
                            <div className="p-3 bg-gray-100 rounded-lg text-gray-700 break-words">
                              {entry.text || entry.content}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  {(entry.role === 'User' || entry.role === 'user') &&
                    (entry.text || entry.content) && (
                      <div className="mb-4">
                        <div className="flex items-start">
                          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 mr-3">
                            <User className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <div className="font-medium text-gray-900">Client</div>
                              <div className="text-xs text-gray-500">
                                {entry.timestamp ? formatTimestamp(entry.timestamp) : ''}
                              </div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg text-gray-700 break-words">
                              {entry.text || entry.content}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>No transcript available for this interview</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function getBadgeClass(qualificationScore: string) {
  const sentiment = getQualificationSentimentType(qualificationScore);
  const styles = getSentimentStyles(sentiment);
  return `${styles.text} ${styles.border}`;
}

export function getDuration(startedAt: string, endedAt: string) {
  if (!startedAt || !endedAt) return 'N/A';

  const duration = new Date(endedAt).getTime() - new Date(startedAt).getTime();

  if (duration <= 0) return 'N/A';

  const minutes = Math.floor(duration / (1000 * 60));
  const seconds = Math.floor((duration % (1000 * 60)) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function getStartTime(startedAt: string) {
  return new Date(startedAt).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
